import React, { useMemo, useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Tooltip, TextField, Button, Chip, List, ListItem, ListItemText, Link, Typography, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { feedbackCategoriesForMode } from '@common/domain/types';
import type { FeedbackCategoryId, FeedbackMode } from '@common/domain/types';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MarkdownContent } from './MarkdownContent';

const GraphIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <circle cx="4" cy="8" r="2" />
        <circle cx="20" cy="8" r="2" />
        <circle cx="4" cy="18" r="2" />
        <circle cx="20" cy="18" r="2" />
        <path d="M9.5 10.5L5.5 8.5" />
        <path d="M14.5 10.5L18.5 8.5" />
        <path d="M9.5 13.5L5.5 16.5" />
        <path d="M14.5 13.5L18.5 16.5" />
    </svg>
);

interface ViewportPopupProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    width: number;
    margin?: number;
    children: React.ReactNode;
}

/**
 * Viewport-aware popup. Renders into document.body via portal with
 * `position: fixed` so it's always positioned relative to the viewport,
 * regardless of any ancestor transforms or scroll. It:
 *   - Opens below the trigger when there is room
 *   - Flips above the trigger when there isn't
 *   - Otherwise picks whichever side has more room and caps maxHeight to fit
 *   - Re-runs the calculation via ResizeObserver every time its inner
 *     content resizes (e.g. an accordion expanding/collapsing), so it
 *     never overflows below the viewport mid-animation.
 */
const ViewportPopup: React.FC<ViewportPopupProps> = ({
    open,
    anchorEl,
    onClose,
    width,
    margin = 16,
    children,
}) => {
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [layout, setLayout] = useState<{
        top: number;
        left: number;
        maxHeight: number;
        ready: boolean;
    }>({ top: 0, left: 0, maxHeight: 0, ready: false });

    const compute = useCallback(() => {
        if (!anchorEl || !popupRef.current) return;
        const anchorRect = anchorEl.getBoundingClientRect();
        const popup = popupRef.current;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const desiredWidth = Math.min(width, vw - margin * 2);
        const popupHeight = popup.offsetHeight;

        const spaceBelow = vh - anchorRect.bottom - margin;
        const spaceAbove = anchorRect.top - margin;

        let top: number;
        let maxHeight: number;
        if (popupHeight <= spaceBelow) {
            top = anchorRect.bottom;
            maxHeight = spaceBelow;
        } else if (popupHeight <= spaceAbove) {
            top = anchorRect.top - popupHeight;
            maxHeight = spaceAbove;
        } else if (spaceBelow >= spaceAbove) {
            top = anchorRect.bottom;
            maxHeight = spaceBelow;
        } else {
            top = margin;
            maxHeight = spaceAbove;
        }

        const desiredCenter = anchorRect.left + anchorRect.width / 2;
        let left = desiredCenter - desiredWidth / 2;
        left = Math.max(margin, Math.min(left, vw - desiredWidth - margin));

        top = Math.max(margin, Math.min(top, vh - margin - Math.min(popupHeight, maxHeight)));

        setLayout({ top, left, maxHeight, ready: true });
    }, [anchorEl, width, margin]);

    useLayoutEffect(() => {
        if (!open) {
            setLayout((prev) => ({ ...prev, ready: false }));
            return;
        }
        compute();
    }, [open, compute]);

    useEffect(() => {
        if (!open || !anchorEl) return;
        const popup = popupRef.current;
        if (!popup) return;

        const ro = new ResizeObserver(() => compute());
        ro.observe(popup);

        const onWindow = () => compute();
        window.addEventListener('resize', onWindow);
        window.addEventListener('scroll', onWindow, true);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', onWindow);
            window.removeEventListener('scroll', onWindow, true);
        };
    }, [open, anchorEl, compute]);

    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (popupRef.current?.contains(target)) return;
            if (anchorEl?.contains(target)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, anchorEl, onClose]);

    if (!open) return null;

    return createPortal(
        <div
            ref={popupRef}
            role="dialog"
            style={{
                position: 'fixed',
                top: layout.top,
                left: layout.left,
                width,
                maxWidth: `calc(100vw - ${margin * 2}px)`,
                maxHeight: layout.ready ? layout.maxHeight : `calc(100vh - ${margin * 2}px)`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 1500,
                visibility: layout.ready ? 'visible' : 'hidden',
                background: 'var(--color-surface, white)',
                color: 'var(--color-text, inherit)',
                borderRadius: 8,
                boxShadow:
                    '0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12)',
            }}
        >
            {children}
        </div>,
        document.body
    );
};

interface FeedbackButtonsProps {
    exchangeId: string;
    seeds?: string[];
    onViewGraph?: (seeds: string[]) => void;
    toolTrace?: unknown;
    showToolTraceIcon?: boolean;
    onFeedback?: (
        exchangeId: string,
        kind: 'up' | 'down' | null,
        comment?: string,
        categories?: FeedbackCategoryId[],
    ) => void;
    textColor?: string;
    iconColor?: string;
    initialRating?: number;
    /** Drives which failure categories are offered. Avatar mode adds the delivery ones. */
    mode?: FeedbackMode;
    initialCategories?: FeedbackCategoryId[];
    initialComment?: string;
}

type AnyObj = Record<string, any>;
function asArray(v: unknown): any[] {
    return Array.isArray(v) ? v : [];
}

function pickUsedLinks(toolTrace: unknown): Array<{ url: string; title?: string; content?: string }> {
    if (!toolTrace || typeof toolTrace !== 'object') return [];
    const t = toolTrace as AnyObj;

    const urls = new Map<string, { url: string; title?: string; content?: string }>();
    const events = asArray(t.events);

    for (const ev of events) {
        const delta = (ev && typeof ev === 'object') ? (ev as AnyObj).delta : undefined;
        const annotations = delta && typeof delta === 'object' ? asArray((delta as AnyObj).annotations) : [];
        for (const a of annotations) {
            const urlCitation = (a as AnyObj)?.url_citation;
            const url = typeof urlCitation?.url === 'string' ? urlCitation.url : undefined;
            if (!url) continue;
            const title = typeof urlCitation?.title === 'string' ? urlCitation.title : undefined;
            const content = typeof urlCitation?.content === 'string' ? urlCitation.content : undefined;
            if (!urls.has(url)) urls.set(url, { url, title, content });
            else if (!urls.get(url)?.content && content) urls.set(url, { url, title: urls.get(url)?.title ?? title, content });
        }
    }

    return Array.from(urls.values());
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
    exchangeId,
    seeds,
    onViewGraph,
    toolTrace,
    showToolTraceIcon,
    onFeedback,
    textColor = 'inherit',
    iconColor = 'var(--color-text-secondary)',
    initialRating,
    mode = 'TEXT',
    initialCategories,
    initialComment,
}) => {
    const [showComment, setShowComment] = useState(false);
    const [pendingKind, setPendingKind] = useState<'up' | 'down' | null>(() => {
        if (initialRating === 1) return 'up';
        if (initialRating === -1) return 'down';
        return null;
    });
    const [comment, setComment] = useState(initialComment ?? '');
    const [categories, setCategories] = useState<FeedbackCategoryId[]>(initialCategories ?? []);
    const availableCategories = useMemo(() => feedbackCategoriesForMode(mode), [mode]);
    const [webAnchorEl, setWebAnchorEl] = useState<HTMLElement | null>(null);
    const webLinks = useMemo(() => pickUsedLinks(toolTrace), [toolTrace]);
    const webOpen = Boolean(webAnchorEl);
    const [expandedWebContentUrl, setExpandedWebContentUrl] = useState<string | null>(null);
    const isWebExpanded = Boolean(expandedWebContentUrl);
    const closeWebPopup = useCallback(() => {
        setWebAnchorEl(null);
        setExpandedWebContentUrl(null);
    }, []);

    // Update internal state if initialRating changes (e.g. when loading a new session)
    useEffect(() => {
        if (initialRating === 1) setPendingKind('up');
        else if (initialRating === -1) setPendingKind('down');
        else setPendingKind(null);
    }, [initialRating]);

    useEffect(() => {
        setCategories(initialCategories ?? []);
    }, [initialCategories]);

    useEffect(() => {
        setComment(initialComment ?? '');
    }, [initialComment]);

    const handleThumbClick = (kind: 'up' | 'down') => {
        let newKind: 'up' | 'down' | null = kind;

        if (pendingKind === kind) {
            if (showComment) {
            newKind = null;
            setShowComment(false);
            setComment('');
            setCategories([]);
            onFeedback?.(exchangeId, null, '', []);
            } else {
            setShowComment(true);
            return;
            }
        } else {
            // switching from up -> down or down -> up
            setComment('');
            setCategories([]);
            setPendingKind(kind);
            setShowComment(true);
            return;
        }

        setPendingKind(newKind);
        };

    const handleCancel = () => {
        setShowComment(false);
        setComment(initialComment ?? '');
        setCategories(initialCategories ?? []);
        if (initialRating === 1) setPendingKind('up');
        else if (initialRating === -1) setPendingKind('down');
        else setPendingKind(null);
    };

    const toggleCategory = (id: FeedbackCategoryId) => {
        setCategories(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        );
    };

    const handleSubmit = () => {
        if (!pendingKind) return;
        // Categories only describe a complaint, so they are never sent with a thumbs-up.
        const submitted = pendingKind === 'down' ? categories : [];
        onFeedback?.(exchangeId, pendingKind, comment, submitted);
        setShowComment(false);
    };

    useEffect(() => {
        if (showComment) {
            const el = document.getElementById(`feedback-${exchangeId}`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [showComment, exchangeId]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {seeds && seeds.length > 0 && onViewGraph && (
                    <Tooltip title="View Knowledge Graph" placement="top">
                        <IconButton
                            size="small"
                            sx={{
                                color: iconColor,
                                '&:hover': { backgroundColor: 'var(--color-hover)' },
                            }}
                            onClick={() => onViewGraph(seeds)}
                        >
                            <GraphIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {showToolTraceIcon && (
                    <>
                        <Tooltip title="View Web Search links" placement="top">
                            <IconButton
                                size="small"
                                sx={{
                                    color: iconColor,
                                    '&:hover': { backgroundColor: 'var(--color-hover)' },
                                }}
                                onClick={(e) => setWebAnchorEl(e.currentTarget)}
                                aria-label="View web search links"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </IconButton>
                        </Tooltip>

                        <ViewportPopup
                            open={webOpen}
                            anchorEl={webAnchorEl}
                            onClose={closeWebPopup}
                            width={isWebExpanded ? 920 : 640}
                        >
                            <Box sx={{ px: 2, pt: 1.25, pb: 1, flex: '0 0 auto' }}>
                                <Typography variant="subtitle2">Sites used</Typography>
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                    Links the model cited while answering.
                                </Typography>
                            </Box>
                            <Divider sx={{ flex: '0 0 auto' }} />
                            {webLinks.length > 0 ? (
                                <List
                                    dense
                                    sx={{
                                        flex: '1 1 auto',
                                        minHeight: 0,
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        px: 1,
                                    }}
                                >
                                    {webLinks.map((l) => (
                                        <ListItem key={l.url} sx={{ py: 0.5, pr: 1, display: 'block' }}>
                                            <ListItemText
                                                primary={
                                                    <Link href={l.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                        {l.title || l.url}
                                                    </Link>
                                                }
                                                secondary={
                                                    l.title ? (
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            sx={{
                                                                color: 'var(--color-text-muted)',
                                                                display: 'block',
                                                                overflowWrap: 'anywhere',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {l.url}
                                                        </Typography>
                                                    ) : null
                                                }
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        overflowWrap: 'anywhere',
                                                        wordBreak: 'break-word',
                                                    },
                                                }}
                                            />

                                            {l.content && (
                                                <Accordion
                                                    disableGutters
                                                    elevation={0}
                                                    expanded={expandedWebContentUrl === l.url}
                                                    onChange={(_e, nextExpanded) => {
                                                        setExpandedWebContentUrl(nextExpanded ? l.url : null);
                                                    }}
                                                    sx={{
                                                        mt: 0.5,
                                                        bgcolor: 'transparent',
                                                        '&:before': { display: 'none' },
                                                    }}
                                                >
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32, '& .MuiAccordionSummary-content': { my: 0 } }}>
                                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                                            View returned content
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                                                        <Box
                                                            sx={{
                                                                m: 0,
                                                                p: 1,
                                                                borderRadius: 1,
                                                                bgcolor: 'rgba(0,0,0,0.04)',
                                                                overflowWrap: 'anywhere',
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            <MarkdownContent content={l.content} sources={{}} />
                                                        </Box>
                                                    </AccordionDetails>
                                                </Accordion>
                                            )}
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                                        No link citations were returned for this web search.
                                    </Typography>
                                </Box>
                            )}
                        </ViewportPopup>
                    </>
                )}

                <IconButton
                    size="small"
                    sx={{
                        color: pendingKind === 'up' ? 'var(--color-accent)' : iconColor,
                        backgroundColor: pendingKind === 'up' ? 'var(--color-accent-soft)' : 'transparent',
                        '&:hover': { backgroundColor: 'var(--color-accent-soft)' },
                    }}
                    onClick={() => handleThumbClick('up')}
                >
                    {pendingKind === 'up' ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOffAltIcon fontSize="small" />}
                </IconButton>

                <IconButton
                    size="small"
                    sx={{
                        color: pendingKind === 'down' ? 'var(--color-accent)' : iconColor,
                        backgroundColor: pendingKind === 'down' ? 'var(--color-accent-soft)' : 'transparent',
                        '&:hover': { backgroundColor: 'var(--color-accent-soft)' },
                    }}
                    onClick={() => handleThumbClick('down')}
                >
                    {pendingKind === 'down' ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOffAltIcon fontSize="small" />}
                </IconButton>
            </Box>

            {showComment && (
                <Box
                    id={`feedback-${exchangeId}`}
                    sx={{
                        mt: 1,
                        pr: 1,
                        pb: 0.5,
                        width: '100%',
                        maxWidth: '420px'
                    }}
                >
                    {pendingKind === 'down' && (
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: textColor, opacity: 0.75, display: 'block', mb: 0.75 }}
                            >
                                What went wrong?
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {availableCategories.map(cat => {
                                    const selected = categories.includes(cat.id);
                                    return (
                                        <Chip
                                            key={cat.id}
                                            label={cat.label}
                                            size="small"
                                            onClick={() => toggleCategory(cat.id)}
                                            aria-pressed={selected}
                                            sx={{
                                                cursor: 'pointer',
                                                color: selected ? 'var(--color-accent)' : textColor,
                                                borderColor: selected
                                                    ? 'var(--color-accent)'
                                                    : 'var(--glass-border-subtle)',
                                                backgroundColor: selected
                                                    ? 'var(--color-accent-soft)'
                                                    : 'transparent',
                                                border: '1px solid',
                                                '&:hover': { backgroundColor: 'var(--color-accent-soft)' },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    <TextField
                        size="small"
                        placeholder={pendingKind === 'down' ? 'Add detail (optional)…' : 'Optional comment…'}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{
                            '& .MuiInputBase-input': { color: textColor },
                            '& .MuiInputBase-input::placeholder': {
                                color: textColor === 'inherit' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)',
                                opacity: 1,
                            },
                            '& .MuiOutlinedInput-root': {
                                minHeight: 72,
                                borderRadius: '14px',
                                backgroundColor: 'var(--color-hover)',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--glass-border-subtle)',
                            },
                        }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            size="small"
                            sx={{ color: textColor }}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: textColor, borderColor: 'var(--glass-border-subtle)' }}
                            onClick={handleSubmit}
                        >
                            Send
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
